/**
 * Builds what the Body Composition overview cards show (#5456) from readings that were already
 * loaded. Pure: no Angular, no database, so a unit test can drive it
 * (`body-comp-overview-model.test.ts`). The loading is `body-comp-overview-data.service.ts`;
 * the drawing is `components/cs-body-comp-overview`.
 *
 * Every trend comes from `@cf-shared/body-comp-overview`, which uses the app's Theil-Sen
 * estimators. Calculations stay in kg, cm and mm; values are converted to the user's units only
 * when they are turned into text or chart points here.
 *
 * See design-docs/coaching-dashboard.md, "The cards".
 */

import {
  FatTrendDirection,
  ProgressStatus,
  RUNNING_WINDOW_DAYS_FAT,
  RUNNING_WINDOW_DAYS_WEIGHT,
  TrendPoint,
  TrendSeries,
  dayIso,
  dayNumber,
  fatTrendDirection,
  kcalAdherence,
  runningTrend,
  straightTrend,
  theilSenChangePerWeek,
  weightChangePercentPerWeek,
} from '@cf-shared/body-comp-overview';
import { CalorieIntakeBaseline, CheckupProgressSnapshot } from '@cf-shared/data-model/log-weekly-checkup';
import { buildRunningTrendRows, RUNNING_TREND_BODY_FAT_OPTIONS, RUNNING_TREND_WEIGHT_OPTIONS, TrendDirection } from '@cf-shared/running-trend';
import { BodyFatSeries, DatedSlopePoint } from '@cf-shared/plan-update-internals';
import { PlanUpdateUserMessage } from '@cf-shared/plan-update-interface';
import { roundToPrecision } from '@cf-shared/shared-util';
import { LL } from 'i18n/i18n.service';
import type { TranslationFunctions } from '../../gensrc/i18n/i18n-types';
import type { TranslatedString } from 'models/translated-string';
import { ProgressStatusRule, progressStatus } from './progress-status';

/** which trend the cards draw */
export enum OverviewTrendKind {
  /** one straight fit over the range (2 weeks) */
  Straight = 'Straight',
  /** the running trend (4 weeks and longer) */
  Running = 'Running',
}

/** one day's body readings, in kg, mm and cm */
export interface OverviewReading {
  dateIso: string;
  weight_kg?: number;
  /** the mean of the skinfold sites the app measures for this user */
  skinfoldMean_mm?: number;
  waist_cm?: number;
  /** each measured skinfold site on its own, keyed by a site id */
  sites?: { [siteId: string]: number };
}

/** one skinfold site the user is measured on, with its display name */
export interface OverviewSkinfoldSite {
  id: string;
  name: TranslatedString;
}

/** the parts of the plan check's result the overview reads */
export interface OverviewPlanCheck {
  needsAdjustment: boolean;
  actionDecided?: boolean;
  message?: PlanUpdateUserMessage;
  estimatedBW_kg?: number;
  weightSlope_kgwk?: number;
  fatnessSlopeBottom?: number;
  fatnessSlopeTop?: number;
  fatnessSeries?: BodyFatSeries;
  weightSamples?: DatedSlopePoint[];
  firstCountedDayIso?: string;
}

/** one day of the check-up week */
export interface OverviewIntakeDay {
  dateIso: string;
  label: TranslatedString;
  consumed: number;
  target: number;
  ticked: boolean;
}

/** the check-up whose week and reported intake cards 2, 4 and 7 show */
export interface OverviewCheckup {
  dateIso: string;
  calorieIntakeBaseline?: number;
  selectedCalorieIntakeBaseline?: CalorieIntakeBaseline;
  /** false when the check-up stored no ticked days: every bar is then solid and no day count is shown */
  hasTickedDays: boolean;
  days: OverviewIntakeDay[];
  /** training days planned and done in that week; undefined when no workout was planned */
  training?: { plannedDays: number; doneDays: number };
}

export interface BodyCompOverviewInput {
  /** first and last calendar day of the range */
  firstDayIso: string;
  lastDayIso: string;
  trendKind: OverviewTrendKind;
  /**
   * true when the cards describe the present moment: the range ends today. Only then may the
   * live status badge and the "at N kcal" figure be shown.
   */
  describesNow: boolean;
  /**
   * readings of the range AND of the weeks before it that the running windows reach back into;
   * with `runningTrendOn`, also of the days after a past range that the running trend uses
   */
  readings: OverviewReading[];
  /**
   * true while the per-account switch `ConfigKey.RunningTrendCharts` is on (#6627): the running
   * branch then draws `computeRunningTrend` with directions and provisional days instead of the
   * interim `runningTrend`. The 2-week straight branch is the same either way.
   */
  runningTrendOn?: boolean;
  /** the app's today, needed only with `runningTrendOn`: days a later reading can still move are marked */
  todayIso?: string;
  skinfoldSites: OverviewSkinfoldSite[];
  /** the plan check that judged the present moment; only read when `describesNow` */
  planCheck?: OverviewPlanCheck;
  statusRule: ProgressStatusRule;
  /** a completed check-up's stored verdict; when given, it replaces the live plan check */
  storedSnapshot?: CheckupProgressSnapshot;
  /**
   * true when the cards describe one completed check-up, not the present. The live plan check is
   * then never read, even when the check-up stores no snapshot (one made before #5456).
   */
  describesCompletedCheckup?: boolean;
  checkup?: OverviewCheckup;
  /**
   * whether the "at N kcal/day" figure is shown. It describes the present or the check-up being
   * viewed, so a host hides it when the user paged back to an earlier period.
   */
  showReportedIntake: boolean;
  /** factor from kg and cm to the user's units, and those units' names */
  weightUnit: { factor: number; unitName: string };
  lengthUnit: { factor: number; unitName: string };
  formatDate: (dateIso: string) => string;
}

export interface OverviewChart {
  points: { dateIso: string; value: number }[];
  /** with the running trend switch on (#6627), each row also carries its tunnel direction and whether it is provisional */
  band: { dateIso: string; low?: number; mid?: number; high?: number; direction?: TrendDirection; provisional?: boolean }[];
  firstDayIso: string;
  lastDayIso: string;
  unitName: string;
}

export interface OverviewMeasureRow {
  name: TranslatedString;
  latestText?: TranslatedString;
  changeText?: TranslatedString;
  chart: OverviewChart;
}

export interface BodyCompOverviewModel {
  progress: {
    status?: ProgressStatus;
    statusText?: TranslatedString;
    trendText: TranslatedString;
    /** the per-week changes of skinfolds and waist, when there are any */
    changeTexts: TranslatedString[];
    footnote?: TranslatedString;
  };
  weightResponse: {
    perWeekText?: TranslatedString;
    percentText?: TranslatedString;
    atKcalText?: TranslatedString;
    intakeSourceText?: TranslatedString;
    /** shown in place of the numbers */
    emptyText?: TranslatedString;
  };
  weight: {
    nowText?: TranslatedString;
    chart: OverviewChart;
    emptyText?: TranslatedString;
  };
  intake?: {
    weekText: TranslatedString;
    averageText?: TranslatedString;
    daysText?: TranslatedString;
    targetText?: TranslatedString;
    /** the highest bar or target, for scaling the bars */
    maxKcal: number;
    targetKcal?: number;
    days: OverviewIntakeDay[];
  };
  /** shown in place of cards 4 and 7 when there is no completed check-up yet */
  noCheckupText?: TranslatedString;
  skinfolds?: {
    rows: OverviewMeasureRow[];
    footnote?: TranslatedString;
  };
  waist?: OverviewMeasureRow;
  /** shown when the user has neither skinfold nor waist readings in the range */
  noFatReadingsText?: TranslatedString;
  adherence?: {
    kcalText?: TranslatedString;
    trainingText: TranslatedString;
  };
}

/** a number with a fixed count of decimals, and an explicit sign when asked */
export function numberText(value: number, decimals: number, signed = false): string {
  const precision = Math.pow(10, -decimals);
  const rounded = roundToPrecision(value, precision);
  // a value that rounds to zero must not read "-0.0"
  const clean = Math.abs(rounded) < precision / 2 ? 0 : rounded;
  const text = clean.toFixed(decimals);
  // the typographic minus matches the rest of the app's signed figures
  if (clean < 0) return `−${text.slice(1)}`;
  return signed && clean > 0 ? `+${text}` : text;
}

interface SeriesTrend {
  chart: OverviewChart;
  /** change per week over the range, in the series' own unit (kg, mm, cm), before conversion */
  changePerWeek?: number;
  /** the trend's value at the end of the range, before conversion */
  endValue?: number;
  /** the latest reading in the range, before conversion */
  latest?: number;
  readingsInRange: number;
}

type DatedValue = { dateIso: string; value: number };

/** what one trend method contributes to a series: the drawn band, the weekly rate and the end value */
interface TrendBranch {
  band: OverviewChart['band'];
  /** change per week, in the series' own unit, before conversion */
  changePerWeek?: number;
  /** the trend's value at the end of the range, before conversion */
  endValue?: number;
}

/** the readings as trend points: day number and value */
function toPoints(list: DatedValue[]): TrendPoint[] {
  return list.map(v => ({ x: dayNumber(v.dateIso), y: v.value }));
}

/**
 * The chart and the per-week change of one series. `fittedSamples`, when given, are the readings
 * the plan check itself fitted: the 2-week chart then draws exactly those, so the picture matches
 * the verdict.
 */
function buildSeriesTrend(params: {
  input: BodyCompOverviewInput;
  values: DatedValue[];
  series: TrendSeries;
  unit: { factor: number; unitName: string };
  fittedSamples?: DatedSlopePoint[];
}): SeriesTrend {
  const { input, values, series, unit, fittedSamples } = params;
  const inRange = values.filter(v => v.dateIso >= input.firstDayIso && v.dateIso <= input.lastDayIso);
  const shown = fittedSamples
    ? fittedSamples.filter(s => !!s?.dateIso && Number.isFinite(s.y)).map(s => ({ dateIso: s.dateIso, value: s.y }))
    : inRange;

  // pick the trend method: 2 weeks is one straight fit; longer ranges use the running trend of
  // #6627 while its switch is on, and the interim running trend otherwise
  let trend: TrendBranch;
  if (input.trendKind === OverviewTrendKind.Straight) {
    trend = straightBranch({ input, shown, series, unit, alreadyJudgedEnough: !!fittedSamples });
  } else if (input.runningTrendOn && input.todayIso) {
    trend = runningBranch({ input, todayIso: input.todayIso, values, inRange, series, unit });
  } else {
    trend = interimRunningBranch({ input, values, series, unit });
  }

  return {
    chart: {
      points: shown.map(v => ({ dateIso: v.dateIso, value: v.value * unit.factor })),
      band: trend.band,
      firstDayIso: input.firstDayIso,
      lastDayIso: input.lastDayIso,
      unitName: unit.unitName,
    },
    changePerWeek: trend.changePerWeek,
    endValue: trend.endValue,
    latest: inRange.length > 0 ? inRange[inRange.length - 1].value : undefined,
    readingsInRange: inRange.length,
  };
}

/** the 2-week trend: one straight fit over the readings shown, drawn from its first to its last day */
function straightBranch(params: {
  input: BodyCompOverviewInput;
  shown: DatedValue[];
  series: TrendSeries;
  unit: { factor: number };
  alreadyJudgedEnough: boolean;
}): TrendBranch {
  const { input, shown, series, unit, alreadyJudgedEnough } = params;
  const firstDay = dayNumber(input.firstDayIso);
  const lastDay = dayNumber(input.lastDayIso);
  const band: OverviewChart['band'] = [];
  let changePerWeek: number | undefined;
  let endValue: number | undefined;

  const trend = straightTrend(toPoints(shown), { firstDay, lastDay, series, alreadyJudgedEnough });
  if (trend) {
    for (const end of [trend.start, trend.end]) {
      band.push({ dateIso: dayIso(end.x), low: end.low * unit.factor, mid: end.mid * unit.factor, high: end.high * unit.factor });
    }
    changePerWeek = trend.slopePerDay * 7;
    endValue = trend.end.mid;
  }
  return { band, changePerWeek, endValue };
}

/** the running trend of #6627, drawn while the `RunningTrendCharts` switch is on */
function runningBranch(params: {
  input: BodyCompOverviewInput;
  todayIso: string;
  values: DatedValue[];
  inRange: DatedValue[];
  series: TrendSeries;
  unit: { factor: number };
}): TrendBranch {
  const { input, todayIso, values, inRange, series, unit } = params;
  const band: OverviewChart['band'] = [];
  let changePerWeek: number | undefined;
  let endValue: number | undefined;

  // one row per day, with its direction and whether a later reading can still move it
  const rows = buildRunningTrendRows(values, {
    firstDayIso: input.firstDayIso,
    lastDayIso: input.lastDayIso,
    todayIso,
  }, series === TrendSeries.Weight ? RUNNING_TREND_WEIGHT_OPTIONS : RUNNING_TREND_BODY_FAT_OPTIONS);
  let hasCurve = false;
  for (const row of rows) {
    if (row.value === undefined) {
      band.push({ dateIso: row.dateIso });
      continue;
    }
    band.push({
      dateIso: row.dateIso,
      low: row.lower * unit.factor,
      mid: row.value * unit.factor,
      high: row.upper * unit.factor,
      direction: row.direction,
      provisional: row.isProvisional,
    });
    hasCurve = true;
    endValue = row.value;
  }
  // the weekly rate is NEVER read off the curve (design-docs/running-trend.md, answer 6): it is
  // one Theil-Sen slope over the readings in the range. A series too thin to draw a curve gets
  // none, so it keeps the "not enough readings" text rather than a rate from 2 readings
  if (hasCurve) {
    changePerWeek = theilSenChangePerWeek(toPoints(inRange));
  }
  return { band, changePerWeek, endValue };
}

/**
 * the interim running trend, drawn while the `RunningTrendCharts` switch is off. Delete this
 * function and its branch in `buildSeriesTrend` once the switch is on for everyone.
 */
function interimRunningBranch(params: {
  input: BodyCompOverviewInput;
  values: DatedValue[];
  series: TrendSeries;
  unit: { factor: number };
}): TrendBranch {
  const { input, values, series, unit } = params;
  const firstDay = dayNumber(input.firstDayIso);
  const lastDay = dayNumber(input.lastDayIso);
  const band: OverviewChart['band'] = [];
  let changePerWeek: number | undefined;

  // the running windows reach back before the range, so they get every reading handed in
  const rows = runningTrend(toPoints(values), {
    firstDay,
    lastDay,
    windowDays: series === TrendSeries.Weight ? RUNNING_WINDOW_DAYS_WEIGHT : RUNNING_WINDOW_DAYS_FAT,
    series,
  });
  let first: { x: number; mid: number } | undefined;
  let last: { x: number; mid: number } | undefined;
  rows.forEach((row, i) => {
    const dateIso = dayIso(firstDay + i);
    if (!row) {
      band.push({ dateIso });
      return;
    }
    band.push({ dateIso, low: row.low * unit.factor, mid: row.mid * unit.factor, high: row.high * unit.factor });
    first = first ?? { x: row.x, mid: row.mid };
    last = { x: row.x, mid: row.mid };
  });
  // the net change of the curve between its first and last day that have a trend
  if (first && last && last.x > first.x) {
    changePerWeek = (last.mid - first.mid) / (last.x - first.x) * 7;
  }
  return { band, changePerWeek, endValue: last?.mid };
}

/** the sentence for one fat-trend direction */
function directionText(direction: FatTrendDirection, ll: TranslationFunctions): TranslatedString {
  switch (direction) {
    case FatTrendDirection.Down: return ll.bodyCompOverview.fatTrendDown();
    case FatTrendDirection.Up: return ll.bodyCompOverview.fatTrendUp();
    case FatTrendDirection.Unclear: return ll.bodyCompOverview.fatTrendUnclear();
    case FatTrendDirection.NotEnough: return ll.bodyCompOverview.notEnoughFatReadings();
  }
}

/** the badge text for one status; the same words the Home goal card uses */
function statusText(status: ProgressStatus, ll: TranslationFunctions): TranslatedString {
  switch (status) {
    case ProgressStatus.OnTrack: return ll.home.progress.goalCard.onTrack();
    case ProgressStatus.OffTrack: return ll.home.progress.goalCard.offTrack();
    case ProgressStatus.Undetermined: return ll.home.progress.goalCard.undetermined();
  }
}

/** how the user chose the intake they reported at a check-up, in words */
function baselineSourceText(selected: CalorieIntakeBaseline | undefined, ll: TranslationFunctions): TranslatedString {
  switch (selected) {
    case CalorieIntakeBaseline.Target: return ll.bodyCompOverview.baselineTarget();
    case CalorieIntakeBaseline.Custom: return ll.bodyCompOverview.baselineCustom();
    default: return ll.bodyCompOverview.baselineLogged();
  }
}

/** a factor to the user's unit, with that unit's name */
interface OverviewUnit {
  factor: number;
  unitName: string;
}

const MILLIMETRES: OverviewUnit = { factor: 1, unitName: 'mm' };

/** "+0.4 mm/wk": a change per week in the user's unit */
function perWeekText(change: number, unit: OverviewUnit, decimals: number, ll: TranslationFunctions): TranslatedString {
  return ll.bodyCompOverview.perWeek({ value: numberText(change * unit.factor, decimals, true), unit: unit.unitName });
}

/**
 * Whose verdict the cards show, decided once so the badge and the numbers beside it cannot
 * disagree. At most one of the two is set:
 * - `snapshot`: the verdict a completed check-up stored;
 * - `liveCheck`: the plan check that judged the present moment. It speaks only for the 14 days
 *   ending today, so it is used only on the straight (2-week) trend of a range that ends today.
 * With neither, the cards show the drawn trend and no badge.
 */
interface VerdictSource {
  snapshot?: CheckupProgressSnapshot;
  liveCheck?: OverviewPlanCheck;
}

function verdictSource(input: BodyCompOverviewInput): VerdictSource {
  if (input.storedSnapshot) {
    return { snapshot: input.storedSnapshot };
  }
  const judgesThisRange = input.describesNow && !input.describesCompletedCheckup
    && input.trendKind === OverviewTrendKind.Straight;
  return { liveCheck: judgesThisRange ? input.planCheck : undefined };
}

/** the three series every card after the first reads */
interface OverviewTrends {
  weight: SeriesTrend;
  skinfoldMean: SeriesTrend;
  waist: SeriesTrend;
}

/** Card 1: the status badge, the fat-trend sentence, the per-week changes and the footnote. */
function buildProgressCard(
  input: BodyCompOverviewInput,
  verdict: VerdictSource,
  trends: OverviewTrends,
  ll: TranslationFunctions,
): BodyCompOverviewModel['progress'] {
  let status: ProgressStatus | undefined;
  let direction: FatTrendDirection;
  let readFrom: BodyFatSeries | undefined;
  let footnote: TranslatedString | undefined;
  // true when the fat line must say weigh-ins are what is missing: the check computes no fat
  // slopes at all without a weight trend
  let weighInsMissing = false;
  if (verdict.snapshot) {
    const snapshot = verdict.snapshot;
    status = snapshot.status;
    direction = snapshot.fatTrend ?? fatTrendDirection(snapshot.fatnessSlopeBottom, snapshot.fatnessSlopeTop);
    readFrom = snapshot.fatnessSeries;
    weighInsMissing = snapshot.weightSlope_kgwk === undefined;
    footnote = ll.bodyCompOverview.storedAtCheckup();
  } else if (verdict.liveCheck) {
    const liveCheck = verdict.liveCheck;
    status = progressStatus(liveCheck, input.statusRule);
    direction = fatTrendDirection(liveCheck.fatnessSlopeBottom, liveCheck.fatnessSlopeTop);
    readFrom = liveCheck.fatnessSeries;
    weighInsMissing = liveCheck.weightSlope_kgwk === undefined;
    if (liveCheck.firstCountedDayIso) {
      footnote = ll.bodyCompOverview.countedFrom({ date: input.formatDate(liveCheck.firstCountedDayIso) });
    }
  } else {
    // no verdict here: the direction is read off the drawn trend, skinfolds first, then waist
    let drawnChange: number | undefined;
    if (trends.skinfoldMean.changePerWeek !== undefined) {
      drawnChange = trends.skinfoldMean.changePerWeek;
      readFrom = BodyFatSeries.Calipers;
    } else if (trends.waist.changePerWeek !== undefined) {
      drawnChange = trends.waist.changePerWeek;
      readFrom = BodyFatSeries.Tape;
    }
    if (drawnChange === undefined) {
      direction = FatTrendDirection.NotEnough;
    } else if (drawnChange < 0) {
      direction = FatTrendDirection.Down;
    } else if (drawnChange > 0) {
      direction = FatTrendDirection.Up;
    } else {
      direction = FatTrendDirection.Unclear;
    }
    // a range ending today is still judged, only on its last 14 days; say so
    footnote = input.describesNow && !input.describesCompletedCheckup
      ? ll.bodyCompOverview.judgedOnLast14Days()
      : ll.bodyCompOverview.periodTrendOnly();
  }

  let trendText = weighInsMissing && direction === FatTrendDirection.NotEnough
    ? ll.bodyCompOverview.notEnoughWeighIns()
    : directionText(direction, ll);
  if (readFrom && direction !== FatTrendDirection.NotEnough) {
    const from = readFrom === BodyFatSeries.Calipers ? ll.bodyCompOverview.readFromSkinfolds() : ll.bodyCompOverview.readFromWaist();
    trendText = `${trendText} · ${from}` as TranslatedString;
  }

  const changeTexts: TranslatedString[] = [];
  if (trends.skinfoldMean.changePerWeek !== undefined) {
    changeTexts.push(ll.bodyCompOverview.changeOf({
      name: ll.common.measurements.skinfolds(),
      change: perWeekText(trends.skinfoldMean.changePerWeek, MILLIMETRES, 1, ll),
    }));
  }
  if (trends.waist.changePerWeek !== undefined) {
    changeTexts.push(ll.bodyCompOverview.changeOf({
      name: ll.common.measurements.waist(),
      change: perWeekText(trends.waist.changePerWeek, input.lengthUnit, 1, ll),
    }));
  }

  return { status, statusText: status ? statusText(status, ll) : undefined, trendText, changeTexts, footnote };
}

/** Card 2: the weight change per week, as a figure and as a percent of body weight, and the reported intake. */
function buildWeightResponseCard(
  input: BodyCompOverviewInput,
  verdict: VerdictSource,
  weightTrend: SeriesTrend,
  ll: TranslationFunctions,
): BodyCompOverviewModel['weightResponse'] {
  // the verdict's own slope and body weight where there is a verdict; otherwise the drawn trend's
  let slope = weightTrend.changePerWeek;
  let bodyWeight = weightTrend.endValue;
  const judged = verdict.snapshot ?? verdict.liveCheck;
  if (judged) {
    slope = judged.weightSlope_kgwk;
    bodyWeight = judged.estimatedBW_kg;
  }

  const card: BodyCompOverviewModel['weightResponse'] = {};
  if (slope === undefined) {
    card.emptyText = ll.bodyCompOverview.notEnoughWeighIns();
  } else {
    card.perWeekText = perWeekText(slope, input.weightUnit, 2, ll);
    const percent = weightChangePercentPerWeek(slope, bodyWeight);
    if (percent !== undefined) {
      card.percentText = ll.bodyCompOverview.percentOfBodyWeight({ value: numberText(percent, 2, true) });
    }
  }
  // "at N kcal" describes the present (or the check-up being viewed), never a paged-back period
  const baseline = input.checkup?.calorieIntakeBaseline;
  if (input.showReportedIntake && input.checkup && typeof baseline === 'number' && Number.isFinite(baseline) && baseline > 0) {
    card.atKcalText = ll.bodyCompOverview.atKcal({ kcal: Math.round(baseline) });
    card.intakeSourceText = ll.bodyCompOverview.intakeReportedOn({
      how: baselineSourceText(input.checkup.selectedCalorieIntakeBaseline, ll),
      date: input.formatDate(input.checkup.dateIso),
    });
  }
  return card;
}

/** Card 3: the weight chart with the latest weigh-in. */
function buildWeightCard(input: BodyCompOverviewInput, weightTrend: SeriesTrend, ll: TranslationFunctions): BodyCompOverviewModel['weight'] {
  const card: BodyCompOverviewModel['weight'] = { chart: weightTrend.chart };
  if (weightTrend.latest !== undefined) {
    card.nowText = ll.bodyCompOverview.weightNow({
      value: numberText(weightTrend.latest * input.weightUnit.factor, 1),
      unit: input.weightUnit.unitName,
    });
  }
  if (weightTrend.chart.points.length === 0) {
    card.emptyText = ll.bodyCompOverview.noWeighIns();
  }
  return card;
}

/** Cards 4 and 7: the check-up week's intake bars and the adherence figures. */
function buildCheckupWeekCards(
  input: BodyCompOverviewInput,
  checkup: OverviewCheckup,
  ll: TranslationFunctions,
): Pick<BodyCompOverviewModel, 'intake' | 'adherence'> {
  // a check-up that stored no ticks counts every day
  const days = checkup.days.map(day => ({ ...day, ticked: checkup.hasTickedDays ? day.ticked : true }));
  const adherence = kcalAdherence(days);
  const maxKcal = days.reduce((max, day) => Math.max(max, day.consumed || 0, day.target || 0), 0);
  const firstDayIso = days.length > 0 ? days[0].dateIso : checkup.dateIso;
  const lastDayIso = days.length > 0 ? days[days.length - 1].dateIso : checkup.dateIso;

  let kcalText: TranslatedString | undefined;
  if (adherence && adherence.meanTarget !== undefined) {
    kcalText = ll.bodyCompOverview.kcalAgainstTarget({
      consumed: Math.round(adherence.meanConsumed),
      target: Math.round(adherence.meanTarget),
      percent: Math.round(adherence.percent),
    });
  } else if (adherence) {
    kcalText = ll.bodyCompOverview.kcalNoTarget({ consumed: Math.round(adherence.meanConsumed) });
  }

  return {
    intake: {
      weekText: ll.bodyCompOverview.checkupWeek({ from: input.formatDate(firstDayIso), to: input.formatDate(lastDayIso) }),
      averageText: adherence ? ll.bodyCompOverview.avgKcalPerDay({ kcal: Math.round(adherence.meanConsumed) }) : undefined,
      daysText: adherence && checkup.hasTickedDays
        ? ll.bodyCompOverview.daysCounted({ counted: adherence.tickedDays, total: adherence.totalDays })
        : undefined,
      targetText: adherence?.meanTarget !== undefined ? ll.bodyCompOverview.targetKcal({ kcal: Math.round(adherence.meanTarget) }) : undefined,
      targetKcal: adherence?.meanTarget,
      maxKcal,
      days,
    },
    adherence: {
      kcalText,
      trainingText: checkup.training
        ? ll.bodyCompOverview.trainingDays({ done: checkup.training.doneDays, planned: checkup.training.plannedDays })
        : ll.bodyCompOverview.noWorkoutsPlanned(),
    },
  };
}

/** one row of the skinfold or waist card: the name, the latest value, the change per week and the small chart */
function measureRow(name: TranslatedString, trend: SeriesTrend, unit: OverviewUnit, ll: TranslationFunctions): OverviewMeasureRow {
  return {
    name,
    latestText: trend.latest !== undefined
      ? ll.bodyCompOverview.valueWithUnit({ value: numberText(trend.latest * unit.factor, 1), unit: unit.unitName })
      : undefined,
    changeText: trend.changePerWeek !== undefined ? perWeekText(trend.changePerWeek, unit, 1, ll) : undefined,
    chart: trend.chart,
  };
}

/** Builds everything the overview cards show. */
export function buildBodyCompOverviewModel(input: BodyCompOverviewInput, ll: TranslationFunctions = LL): BodyCompOverviewModel {
  const readings = [...(input.readings || [])].filter(r => !!r?.dateIso).sort((a, b) => a.dateIso < b.dateIso ? -1 : 1);
  // one value per reading of the series `pick` names, leaving out readings without that value
  const valuesOf = (pick: (r: OverviewReading) => number | undefined) => readings
    .map(r => ({ dateIso: r.dateIso, value: pick(r) }))
    .filter((v): v is { dateIso: string; value: number } => typeof v.value === 'number' && Number.isFinite(v.value) && v.value > 0);
  const isInRange = (v: { dateIso: string }) => v.dateIso >= input.firstDayIso && v.dateIso <= input.lastDayIso;

  const verdict = verdictSource(input);
  const trends: OverviewTrends = {
    weight: buildSeriesTrend({
      input,
      values: valuesOf(r => r.weight_kg),
      series: TrendSeries.Weight,
      unit: input.weightUnit,
      // draw the check's own weigh-ins only when it really fitted a trend through them
      fittedSamples: verdict.liveCheck?.weightSlope_kgwk !== undefined ? verdict.liveCheck.weightSamples : undefined,
    }),
    skinfoldMean: buildSeriesTrend({ input, values: valuesOf(r => r.skinfoldMean_mm), series: TrendSeries.BodyFat, unit: MILLIMETRES }),
    waist: buildSeriesTrend({ input, values: valuesOf(r => r.waist_cm), series: TrendSeries.BodyFat, unit: input.lengthUnit }),
  };

  const model: BodyCompOverviewModel = {
    progress: buildProgressCard(input, verdict, trends, ll),
    weightResponse: buildWeightResponseCard(input, verdict, trends.weight, ll),
    weight: buildWeightCard(input, trends.weight, ll),
  };

  // cards 4 and 7 need a check-up week to show
  if (input.checkup) {
    const week = buildCheckupWeekCards(input, input.checkup, ll);
    model.intake = week.intake;
    model.adherence = week.adherence;
  } else {
    model.noCheckupText = ll.bodyCompOverview.fillsAfterFirstCheckup();
  }

  // cards 5 and 6: skinfolds and waist, each only for a user who has such readings in the range
  const sites = input.skinfoldSites || [];
  const hasSkinfolds = trends.skinfoldMean.readingsInRange > 0
    || sites.some(site => valuesOf(r => r.sites?.[site.id]).some(isInRange));
  const hasWaist = trends.waist.readingsInRange > 0;
  if (hasSkinfolds) {
    const rows = sites.map(site => measureRow(
      site.name,
      buildSeriesTrend({ input, values: valuesOf(r => r.sites?.[site.id]), series: TrendSeries.BodyFat, unit: MILLIMETRES }),
      MILLIMETRES,
      ll,
    ));
    rows.push(measureRow(ll.bodyCompOverview.mean(), trends.skinfoldMean, MILLIMETRES, ll));
    model.skinfolds = {
      rows,
      footnote: trends.skinfoldMean.changePerWeek === undefined
        ? ll.bodyCompOverview.notEnoughFatReadings()
        : ll.bodyCompOverview.readingsInRange({ count: trends.skinfoldMean.readingsInRange }),
    };
  }
  if (hasWaist) {
    model.waist = measureRow(ll.common.measurements.waist(), trends.waist, input.lengthUnit, ll);
  }
  if (!hasSkinfolds && !hasWaist) {
    model.noFatReadingsText = ll.bodyCompOverview.noFatReadings();
  }

  return model;
}
